/* eslint-disable @typescript-eslint/no-deprecated */
import { type MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import {
  XmarkIcon,
  OpenSidebarIcon,
  CloseSidebarIcon,
} from '@dynatrace/strato-icons';

import { usePageContext } from './page-context.js';
import { usePanelContext } from './panel-context.js';
import { Button, ButtonProps } from '../../buttons/button/Button.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { isDev } from '../../core/utils/is-node-environment.js';

/**
 * @public
 * @deprecated Deprecated along with the `Page` component and will be removed in a future release.
 */
export interface PanelControlButtonProps
  extends Omit<ButtonProps<'button'>, 'as' | 'type' | 'loading'> {
  /** The action triggered by the button. */
  action?: 'toggle' | 'open' | 'close';
  /** The target panel controlled by the button. */
  target?: 'sidebar' | 'details';
}

/**
 * Use the `PanelControlButton` to get the correct icon for toggling the sidebar / detail view.
 * Depending on where it is rendered and the current state of the panel, the corresponding icon is rendered.
 * You can customize the rendered content by passing any children.
 * @public
 * @deprecated The `Page.PanelControlButton` component is deprecated along with the `Page` component and will be removed in a future release.
 */
export const PanelControlButton = (props: PanelControlButtonProps) => {
  const {
    action: actionProp,
    target: targetProp,
    children: childrenProp,
    onClick,
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  }: PanelControlButtonProps = props;

  const intl = useIntl();
  const pageCtx = usePageContext();
  const panelCtx = usePanelContext();

  const targetPanel = targetProp ?? panelCtx?.name ?? 'details';
  const action = actionProp ?? (panelCtx !== null ? 'close' : 'toggle');
  const panel = pageCtx.panels[targetPanel];

  const isPanelControlled = panel?.isControlled;
  const isPanelDismissed = panel?.isDismissed;
  const shouldOpen =
    action === 'open' || (action === 'toggle' && isPanelDismissed === true);

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const defaultDataDtProperties = `state:${shouldOpen ? 'expand' : 'collapse'}`;
  const {
    // Reads consumer's data-dt-name prop and forwards it as data-dt-children-name on the Button.
    'data-dt-name': dataDtName = 'ExpandCollapse',
    'data-dt-properties': dataDtProperties = defaultDataDtProperties,
    ...remainingBehavioralTrackingProps
  } = behavioralTrackingProps;

  const handleClick = (event: MouseEvent) => {
    if (isPanelControlled && !onClick && isDev()) {
      // eslint-disable-next-line no-console
      console.warn(
        "You are using the panel control button with a controlled panel, but didn't provide an 'onClick' callback. Implement the 'onClick' to update the panel state with the panel control button.",
      );
      return;
    }

    onClick?.(event);

    if (isPanelControlled) {
      return;
    }

    switch (action) {
      case 'open':
        pageCtx.expandPanel(targetPanel);
        break;
      case 'close':
        pageCtx.dismissPanel(targetPanel);
        break;
      case 'toggle':
        pageCtx.togglePanel(targetPanel);
        break;
    }
  };

  let localizedPanel = intl.formatMessage({
    defaultMessage: 'Sidebar',
    id: 'MYBFw8pxHVA1QTDj',
    description: 'Panel name translation',
  });
  if (targetPanel === 'details') {
    localizedPanel = intl.formatMessage({
      defaultMessage: 'Detail View',
      id: '0ntbampPIxNnacS8',
      description: 'Panel name translation',
    });
  }

  let ariaLabel = intl.formatMessage(
    {
      defaultMessage: 'Close {panelName}',
      id: 'QsfELKqpB23/KKWM',
      description: 'Label for the panel control button.',
    },
    { panelName: localizedPanel },
  );
  if (shouldOpen) {
    ariaLabel = intl.formatMessage(
      {
        defaultMessage: 'Open {panelName}',
        id: '01469lp26TkXjPEz',
        description: 'Label for the panel control button.',
      },
      { panelName: localizedPanel },
    );
  }

  let defaultIcon;
  if (shouldOpen) {
    defaultIcon = <OpenSidebarIcon />;
  } else if (action === 'toggle') {
    defaultIcon = <CloseSidebarIcon />;
  } else {
    defaultIcon = <XmarkIcon />;
  }

  return (
    <Button
      aria-label={ariaLabel}
      className={consumerClassName}
      style={consumerStyle}
      onClick={handleClick}
      data-dt-children-name={dataDtName}
      data-dt-properties={dataDtProperties}
      {...remainingBehavioralTrackingProps}
      {...restProps}
    >
      {childrenProp ?? (
        <Button.Prefix>
          <div
            style={{
              rotate: targetPanel === 'sidebar' ? '0deg' : '180deg',
              lineHeight: 0,
            }}
          >
            {defaultIcon}
          </div>
        </Button.Prefix>
      )}
    </Button>
  );
};
(
  PanelControlButton as typeof PanelControlButton & { displayName: string }
).displayName = 'Page.PanelControlButton';
